import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { studioFetch } from '@/studio/api';
import type { DealCriteria } from '../../../../schemas/realsignal/dealCriteria';

export type LocationConfidence = 'high' | 'low';

export interface MapPin {
    propertyId: string;
    lat: number;
    lon: number;
    location_confidence: LocationConfidence;
    status?: string;
    auction_date?: string;
    opening_bid?: number;
    criteriaMatches: string[];
}

interface MapPinsResponse {
    success: boolean;
    pins?: MapPin[];
}

interface LatLngLiteral {
    lat: number;
    lng: number;
}

interface GoogleLatLng {
    lat: () => number;
    lng: () => number;
}

interface GoogleLatLngBounds {
    getNorthEast: () => GoogleLatLng;
    getSouthWest: () => GoogleLatLng;
}

interface GoogleMapOptions {
    center: LatLngLiteral;
    zoom: number;
    mapTypeControl?: boolean;
    streetViewControl?: boolean;
    fullscreenControl?: boolean;
    clickableIcons?: boolean;
    gestureHandling?: string;
}

interface GoogleMap {
    getBounds: () => GoogleLatLngBounds | null;
}

interface GoogleMarker {
    setMap: (map: GoogleMap | null) => void;
}

interface GoogleMapsListener {
    remove?: () => void;
}

interface GoogleMapsEvent {
    addListener: (
        instance: GoogleMap | GoogleMarker,
        eventName: string,
        handler: () => void
    ) => GoogleMapsListener;
    removeListener: (listener: GoogleMapsListener) => void;
}

interface GoogleMapsApi {
    maps: {
        Map: new (element: HTMLElement, options: GoogleMapOptions) => GoogleMap;
        Marker: new (options: {
            map: GoogleMap;
            position: LatLngLiteral;
            title?: string;
        }) => GoogleMarker;
        event: GoogleMapsEvent;
    };
}

interface BoundingBox {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
}

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

declare global {
    interface Window {
        google?: GoogleMapsApi;
        __googleMapsPromise?: Promise<GoogleMapsApi>;
    }
}

export interface RealSignalMapState {
    mapContainerRef: RefObject<HTMLDivElement>;
    pins: MapPin[];
    selectedPin: MapPin | null;
    setSelectedPin: (value: MapPin | null) => void;
    loading: boolean;
    error: string | null;
    mapReady: boolean;
}

const DEFAULT_CENTER: LatLngLiteral = { lat: 29.7604, lng: -95.3698 };
const DEFAULT_ZOOM = 11;
const DEFAULT_LIMIT = 1000;
const REFRESH_DEBOUNCE_MS = 350;
const NOT_DEPLOYED_MESSAGE = 'RealSignal is not deployed in this environment.';

class MapPinsUnavailableError extends Error {
    constructor(message: string = NOT_DEPLOYED_MESSAGE) {
        super(message);
        this.name = 'MapPinsUnavailableError';
    }
}

export function buildStreetViewUrl(apiKey: string | undefined, lat: number, lon: number): string {
    if (apiKey) {
        return `https://www.google.com/maps/embed/v1/streetview?key=${encodeURIComponent(
            apiKey
        )}&location=${lat},${lon}&fov=80`;
    }
    return `https://maps.google.com/maps?q=${lat},${lon}&layer=c&cbll=${lat},${lon}&cbp=11,0,0,0,0&output=embed`;
}

function getBounds(map: GoogleMap): BoundingBox | null {
    const bounds = map.getBounds();
    if (!bounds) return null;
    const northeast = bounds.getNorthEast();
    const southwest = bounds.getSouthWest();
    return {
        minLat: southwest.lat(),
        minLon: southwest.lng(),
        maxLat: northeast.lat(),
        maxLon: northeast.lng(),
    };
}

function loadGoogleMaps(apiKey: string): Promise<GoogleMapsApi> {
    if (window.google?.maps) {
        return Promise.resolve(window.google);
    }
    if (window.__googleMapsPromise) {
        return window.__googleMapsPromise;
    }
    window.__googleMapsPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.google?.maps) {
                resolve(window.google);
            } else {
                reject(new Error('Google Maps failed to initialize.'));
            }
        };
        script.onerror = () => reject(new Error('Google Maps script failed to load.'));
        document.head.appendChild(script);
    });
    return window.__googleMapsPromise;
}

function parsePinsResponse(data: unknown): MapPin[] {
    if (!data || typeof data !== 'object') return [];
    const response = data as MapPinsResponse;
    if (!response.success || !Array.isArray(response.pins)) return [];
    return response.pins
        .filter(pin => pin && typeof pin.lat === 'number' && typeof pin.lon === 'number')
        .map(pin => ({
            ...pin,
            criteriaMatches: Array.isArray(pin.criteriaMatches) ? pin.criteriaMatches : [],
        }));
}

function encodeCriteria(criteria: DealCriteria): string {
    const raw = JSON.stringify(criteria);
    if (typeof globalThis.btoa === 'function') {
        return globalThis.btoa(raw);
    }
    throw new Error('Base64 encoder unavailable for criteria payload.');
}

function normalizeCriteria(criteria: DealCriteria | undefined): DealCriteria | null {
    if (!criteria) return null;
    const entries = Object.entries(criteria).filter(([, value]) => value !== undefined);
    if (entries.length === 0) return null;
    return Object.fromEntries(entries) as DealCriteria;
}

function buildPinsQuery(
    bounds: BoundingBox,
    includeApproximate: boolean,
    criteria: DealCriteria | undefined
): URLSearchParams {
    const params = new URLSearchParams({
        county: 'harris',
        minLat: bounds.minLat.toString(),
        maxLat: bounds.maxLat.toString(),
        minLon: bounds.minLon.toString(),
        maxLon: bounds.maxLon.toString(),
        includeApproximate: includeApproximate ? '1' : '0',
        limit: DEFAULT_LIMIT.toString(),
    });
    const normalized = normalizeCriteria(criteria);
    if (normalized) {
        params.set('criteria', encodeCriteria(normalized));
    }
    return params;
}

export async function fetchMapPins(
    bounds: BoundingBox,
    includeApproximate: boolean,
    criteria: DealCriteria | undefined,
    fetcher: Fetcher = studioFetch
): Promise<MapPin[]> {
    const params = buildPinsQuery(bounds, includeApproximate, criteria);
    const response = await fetcher(`/api/realsignal/map-pins?${params.toString()}`);
    if (response.status === 404) {
        throw new MapPinsUnavailableError();
    }
    if (!response.ok) {
        throw new Error(`Map pins request failed (${response.status}).`);
    }
    const data = (await response.json()) as unknown;
    return parsePinsResponse(data);
}

function syncMarkers(
    google: GoogleMapsApi | null,
    map: GoogleMap | null,
    markersRef: RefObject<Map<string, GoogleMarker>>,
    pins: MapPin[],
    onSelect: (pin: MapPin) => void
): void {
    if (!google || !map) return;
    const nextIds = new Set(pins.map(pin => pin.propertyId));
    markersRef.current.forEach((marker, id) => {
        if (!nextIds.has(id)) {
            marker.setMap(null);
            markersRef.current.delete(id);
        }
    });
    pins.forEach(pin => {
        if (markersRef.current.has(pin.propertyId)) return;
        const marker = new google.maps.Marker({
            map,
            position: { lat: pin.lat, lng: pin.lon },
            title: pin.propertyId,
        });
        google.maps.event.addListener(marker, 'click', () => {
            onSelect(pin);
        });
        markersRef.current.set(pin.propertyId, marker);
    });
}

interface PinsRefreshContext {
    mapRef: RefObject<GoogleMap | null>;
    googleRef: RefObject<GoogleMapsApi | null>;
    includeApproximateRef: RefObject<boolean>;
    criteriaRef: RefObject<DealCriteria | undefined>;
    unavailableRef: RefObject<boolean>;
    markersRef: RefObject<Map<string, GoogleMarker>>;
    selectedPin: MapPin | null;
    setPins: Dispatch<SetStateAction<MapPin[]>>;
    setSelectedPin: Dispatch<SetStateAction<MapPin | null>>;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setError: Dispatch<SetStateAction<string | null>>;
}

async function refreshPinsWithContext(context: PinsRefreshContext): Promise<void> {
    const {
        mapRef,
        googleRef,
        includeApproximateRef,
        criteriaRef,
        unavailableRef,
        markersRef,
        selectedPin,
    } = context;
    const { setPins, setSelectedPin, setLoading, setError } = context;
    const map = mapRef.current;
    if (!map || unavailableRef.current) return;
    const bounds = getBounds(map);
    if (!bounds) return;
    setLoading(true);
    setError(null);
    try {
        const nextPins = await fetchMapPins(
            bounds,
            includeApproximateRef.current,
            criteriaRef.current
        );
        setPins(nextPins);
        syncMarkers(googleRef.current, map, markersRef, nextPins, setSelectedPin);
        if (selectedPin && !nextPins.find(pin => pin.propertyId === selectedPin.propertyId)) {
            setSelectedPin(null);
        }
    } catch (err) {
        if (err instanceof MapPinsUnavailableError) {
            unavailableRef.current = true;
        }
        const message = err instanceof Error ? err.message : 'Failed to load map pins.';
        setError(message);
    } finally {
        setLoading(false);
    }
}

function scheduleRefreshWithRef(
    fetchTimeoutRef: RefObject<number | null>,
    refresh: () => void
): void {
    if (fetchTimeoutRef.current) {
        window.clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = window.setTimeout(() => {
        refresh();
    }, REFRESH_DEBOUNCE_MS);
}

interface PinsControllerParams {
    mapRef: RefObject<GoogleMap | null>;
    googleRef: RefObject<GoogleMapsApi | null>;
    includeApproximate: boolean;
    criteria: DealCriteria | undefined;
    setError: Dispatch<SetStateAction<string | null>>;
}

interface PinsControllerState {
    pins: MapPin[];
    selectedPin: MapPin | null;
    setSelectedPin: Dispatch<SetStateAction<MapPin | null>>;
    loading: boolean;
    scheduleRefresh: () => void;
}

function usePinsController(params: PinsControllerParams): PinsControllerState {
    const { mapRef, googleRef, includeApproximate, criteria, setError } = params;
    const markersRef = useRef<Map<string, GoogleMarker>>(new Map());
    const fetchTimeoutRef = useRef<number | null>(null);
    const includeApproximateRef = useRef<boolean>(includeApproximate);
    const criteriaRef = useRef<DealCriteria | undefined>(criteria);
    const unavailableRef = useRef<boolean>(false);
    const [pins, setPins] = useState<MapPin[]>([]);
    const [selectedPin, setSelectedPin] = useState<MapPin | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const refreshPinsForBounds = useCallback(() => {
        void refreshPinsWithContext({
            mapRef,
            googleRef,
            includeApproximateRef,
            criteriaRef,
            unavailableRef,
            markersRef,
            selectedPin,
            setPins,
            setSelectedPin,
            setLoading,
            setError,
        });
    }, [mapRef, googleRef, selectedPin, setError]);

    const scheduleRefresh = useCallback(() => {
        if (unavailableRef.current) return;
        scheduleRefreshWithRef(fetchTimeoutRef, refreshPinsForBounds);
    }, [refreshPinsForBounds]);

    useEffect(() => {
        includeApproximateRef.current = includeApproximate;
        if (mapRef.current && !unavailableRef.current) {
            refreshPinsForBounds();
        }
    }, [includeApproximate, mapRef, refreshPinsForBounds]);

    useEffect(() => {
        criteriaRef.current = criteria;
        if (mapRef.current && !unavailableRef.current) {
            refreshPinsForBounds();
        }
    }, [criteria, mapRef, refreshPinsForBounds]);

    return { pins, selectedPin, setSelectedPin, loading, scheduleRefresh };
}

interface MapInitParams {
    google: GoogleMapsApi;
    container: HTMLDivElement;
    mapRef: RefObject<GoogleMap | null>;
    onIdle: () => void;
    setMapReady: Dispatch<SetStateAction<boolean>>;
}

function initializeMapInstance(params: MapInitParams): GoogleMapsListener {
    const { google, container, mapRef, onIdle, setMapReady } = params;
    if (!mapRef.current) {
        mapRef.current = new google.maps.Map(container, {
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            gestureHandling: 'greedy',
        });
    }
    const listener = google.maps.event.addListener(mapRef.current, 'idle', onIdle);
    setMapReady(true);
    return listener;
}

interface MapLoaderParams {
    apiKey: string | undefined;
    mapContainerRef: RefObject<HTMLDivElement>;
    mapRef: RefObject<GoogleMap | null>;
    googleRef: RefObject<GoogleMapsApi | null>;
    onIdle: () => void;
    setError: Dispatch<SetStateAction<string | null>>;
}

function useGoogleMapLoader(params: MapLoaderParams): boolean {
    const { apiKey, mapContainerRef, mapRef, googleRef, onIdle, setError } = params;
    const [mapReady, setMapReady] = useState<boolean>(false);

    useEffect(() => {
        if (!apiKey) {
            setError('Missing Google Maps API key (VITE_GOOGLE_MAPS_API_KEY).');
            return;
        }
        if (!mapContainerRef.current) return;

        let listener: GoogleMapsListener | null = null;
        let cancelled = false;

        loadGoogleMaps(apiKey)
            .then(google => {
                if (cancelled || !mapContainerRef.current) return;
                googleRef.current = google;
                listener = initializeMapInstance({
                    google,
                    container: mapContainerRef.current,
                    mapRef,
                    onIdle,
                    setMapReady,
                });
            })
            .catch(err => {
                const message = err instanceof Error ? err.message : 'Google Maps failed to load.';
                setError(message);
            });

        return () => {
            cancelled = true;
            if (listener && googleRef.current) {
                googleRef.current.maps.event.removeListener(listener);
            }
        };
    }, [apiKey, mapContainerRef, mapRef, googleRef, onIdle, setError]);

    return mapReady;
}

export function useRealSignalMap(
    apiKey: string | undefined,
    criteria: DealCriteria | undefined,
    includeApproximate: boolean
): RealSignalMapState {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<GoogleMap | null>(null);
    const googleRef = useRef<GoogleMapsApi | null>(null);
    const [error, setError] = useState<string | null>(null);
    const pinsController = usePinsController({
        mapRef,
        googleRef,
        includeApproximate,
        criteria,
        setError,
    });
    const mapReady = useGoogleMapLoader({
        apiKey,
        mapContainerRef,
        mapRef,
        googleRef,
        onIdle: pinsController.scheduleRefresh,
        setError,
    });

    return {
        mapContainerRef,
        pins: pinsController.pins,
        selectedPin: pinsController.selectedPin,
        setSelectedPin: pinsController.setSelectedPin,
        loading: pinsController.loading,
        error,
        mapReady,
    };
}
